/**
 * Shared device constants for form builder, site builder, and embed system.
 * Single source of truth for device width mappings.
 */

/**
 * Maps device mode to max-width CSS value.
 * - phone: 375px (iPhone SE width)
 * - tablet: 768px (iPad portrait width)
 * - desktop: 100% (no constraint)
 */
export const DEVICE_WIDTHS = {
    phone: '375px',
    tablet: '768px',
    desktop: '100%',
};

/**
 * Valid device parameter values for embed URLs.
 */
export const DEVICE_VALUES = ['phone', 'tablet', 'desktop'];

/**
 * Human-readable labels for each device mode, used in embed UI dropdowns.
 */
export const DEVICE_LABELS = {
    phone: 'Phone (375px)',
    tablet: 'Tablet (768px)',
    desktop: 'Desktop (Full Width)',
};

/**
 * Default device mode when no parameter is provided or the value is invalid.
 * 'desktop' means 100% width (no constraint) — same visual behavior as current.
 */
export const DEFAULT_DEVICE = 'desktop';

/**
 * Resolve a device parameter value to a normalized device mode.
 * Returns 'desktop' for unknown/empty values.
 * @param {string|null|undefined} raw - The raw device param value
 * @returns {string} One of DEVICE_VALUES
 */
export function resolveDeviceMode(raw) {
    if (!raw) return DEFAULT_DEVICE;
    const normalized = String(raw).toLowerCase().trim();
    if (DEVICE_VALUES.includes(normalized)) return normalized;
    return DEFAULT_DEVICE;
}

/**
 * Get the max-width CSS value for a device mode.
 * @param {string} mode - Device mode (phone|tablet|desktop)
 * @returns {string} CSS max-width value (e.g. '375px', '768px', '100%')
 */
export function getDeviceMaxWidth(mode) {
    return DEVICE_WIDTHS[mode] || DEVICE_WIDTHS[DEFAULT_DEVICE];
}
